import type {
  CardBlock,
  ReplyState,
  InboxBlock,
  SupportRequestCard,
} from "@go-novus/admin-api";
import { readReplyContent, type ReplyContentBlock } from "@go-novus/admin-api";
import type { VerificationPrompt } from "../conversation/verification";
import type { MessageTheme, WidgetTokens } from "./theme";

/**
 * 卡上的一个商品。三个 id 只为透传而存在：后端发什么就留什么，本地不合成也不裁剪。
 * 都是可选的——历史快照是无版本存储，上个版本存下的卡片没有这几个键。
 */
export interface WidgetProduct {
  imageUrl?: string;
  itemId?: string;
  parentItemId?: string;
  price: string;
  title: string;
  url?: string;
  variantId?: string;
}

/** 一行「名称：数值」，原文由后端给，挂件不翻译、不换算。 */
export interface WidgetLabeledValue {
  label: string;
  value: string;
}

/** 尺码卡（PRD 2.4）：推荐码、对应美码、三围和来源；推荐理由在正文里。 */
export interface WidgetSizeCard {
  measurements: WidgetLabeledValue[];
  recommendedSize: string;
  /** 买家可见的来源原文，如 `Size chart · Alpine Shell`。 */
  source?: string;
  usSize?: string;
}

export interface WidgetCompareColumn {
  title: string;
  url?: string;
}

export interface WidgetCompareRow {
  label: string;
  values: [string, string];
}

/** 对比卡（PRD 2.4）：固定两列，每行是同一个字段在两边的值。 */
export interface WidgetCompareCard {
  columns: [WidgetCompareColumn, WidgetCompareColumn];
  rows: WidgetCompareRow[];
}

/**
 * 加购回执：买家在聊天里加购或改数量、Shopify 确认之后挂件插的一条本地消息。
 * 件数全部来自 Shopify 的返回；不发给后端，也不进后台回放。
 */
export interface WidgetCartReceipt {
  kind: "added" | "updated";
  title: string;
  /** 变体标题，如 `White / M`；默认变体没有。 */
  variant?: string;
  imageUrl?: string;
  variantId: number;
  /** 购物车行 key：改数量时用它找到这一行。 */
  lineKey?: string;
  /** 这一行写完后的件数。 */
  quantity: number;
  /** 这一次加进去的件数；改数量时没有。 */
  added?: number;
  /** 买家要的件数：加购是要加几件，改数量是要改成几件。 */
  requested: number;
}

/**
 * system 是挂件本地插入的系统提示，不算 AI 消息，也不发给后端：换了商品时的 Now viewing 单行灰字，
 * 以及带 cartReceipt 的加购回执。
 */
export type WidgetMessageRole = "ai" | "user" | "system";

/**
 * 一条消息，形状对着 `@go-novus/admin-api` 的终态回复：正文加挂件，挂件之间不互斥；
 * 结构化正文按块展示；transfer 内部正文隐藏，求助卡由会话 owner 经 BFF 打开，原始正文仍保留。
 * 加新挂件先改那边的契约，别只在这里开个字段——店面画得出来后台画不出来就是两套东西。
 * `typing`、`alert` 是本地渲染态（处理中那一行、请求失败那一行），不进快照也不进契约；
 * `failed` 是挂件自己记的「后端没收下」，跟快照走，但同样不进契约。
 */
export interface WidgetChatMessage {
  /** 原任务的 Shopify 登录提示；只带 BFF 请求引用，后台回放没有可执行动作。 */
  verification?: VerificationPrompt;
  blocks?: CardBlock[];
  blocksFailed?: boolean;
  replyStates?: ReplyState[];
  disabledBlocks?: string[];
  maxTaskVersion?: number;
  supportRequests?: SupportRequestCard[];
  supportOpening?: {
    pending: boolean;
    ref?: string;
    orderReference?: string;
    error?: string;
  };
  /** 请求失败那一行：接在处理中那一行的位置，text 是出错文案。 */
  alert?: boolean;
  cartReceipt?: WidgetCartReceipt;
  compareCard?: WidgetCompareCard;
  /** 后端没收下：气泡不能再装成已发出。 */
  failed?: boolean;
  /** 业务卡快照：目前只有后台 Inbox 回放会带，店面消息协议不下发这个字段。 */
  replayBlocks?: InboxBlock[];
  id: string;
  products?: WidgetProduct[];
  role: WidgetMessageRole;
  sizeCard?: WidgetSizeCard;
  source?: string;
  /** 原始正文保留在消息中，渲染时统一解析，换页恢复不丢块类型。 */
  text?: string;
  /** plain 用于本地原文；blocks 用于服务端买家历史；AI 正文默认解析消息块和 Markdown。 */
  textFormat?: "plain" | "blocks";
  typing?: boolean;
}

export interface ResolvedProduct extends WidgetProduct {
  hasImage: boolean;
}

export interface ResolvedCompareRow extends WidgetCompareRow {
  /** 两边的值不同：这一行要高亮。 */
  differs: boolean;
}

export interface ResolvedCompareCard {
  columns: WidgetCompareCard["columns"];
  rows: ResolvedCompareRow[];
}

/** 忽略大小写和多余空白：`20K mm` 和 `20k  mm` 是同一个值，不该被标成差异。 */
function sameValue(first: string, second: string): boolean {
  const normalize = (value: string) =>
    value.trim().replace(/\s+/g, " ").toLowerCase();
  return normalize(first) === normalize(second);
}

/** 差异由挂件判断：后端只给两边的原文，快照和后台回放恢复出来的卡片按同一条规则高亮。 */
export function resolveCompareCard(
  card: WidgetCompareCard,
): ResolvedCompareCard {
  return {
    columns: card.columns,
    rows: card.rows.map((row) => ({
      ...row,
      differs: !sameValue(row.values[0], row.values[1]),
    })),
  };
}

/** 面板算好的一条消息：数据 + 主题 token，消息组件不自己算颜色。 */
export interface ResolvedMessage {
  align: "flex-end" | "flex-start";
  background: string;
  blocks: ReplyContentBlock[];
  color: string;
  compareCard?: ResolvedCompareCard;
  /** 入场延迟：打开面板时已在的消息逐条错开，之后才进来的为 0，只有生成中那一行晚 120ms。 */
  delay: string;
  /** 换页、刷新后醒来的消息：先是骨架，到点从下往上浮起，不走打开聊天窗的时间轴。 */
  wake: boolean;
  lineHeight: number;
  maxWidth: string;
  message: WidgetChatMessage;
  padding: string;
  products: ResolvedProduct[];
  radius: string;
  showText: boolean;
  tokens: WidgetTokens;
}

/** 渐入最多错开这么多条，再长的历史一次性出来。 */
const MAX_STAGGERED = 8;
const STAGGER_STEP_MS = 60;
const ENTER_DELAY_MS = 280;
/** 设计稿 12a：新页面首帧先摆骨架，150ms 起从最新一条往上每条错开 30ms。 */
const WAKE_DELAY_MS = 150;
const WAKE_STEP_MS = 30;
/** 设计稿 11a：买家发出后 120ms，生成中那一行才浮起。 */
const THINKING_DELAY_MS = 120;

/**
 * 消息在开窗时间轴上的位置，只给打开面板那一刻已在的消息。
 * 买家点开聊天窗时从上往下数（10a）；换页、刷新后聊天窗本来就开着，从最新一条往上数（12a）。
 */
export interface MessageEntrance {
  order: number;
  wake?: boolean;
}

function readTokens(theme: MessageTheme): WidgetTokens {
  return {
    accentFill: theme.accentFill,
    accentFillFg: theme.accentFillFg,
    accentSoft: theme.accentSoft,
    accentText: theme.accentText,
    badgeBg: theme.badgeBg,
    badgeFg: theme.badgeFg,
    bubbleBg: theme.bubbleBg,
    bubbleRadius: theme.bubbleRadius,
    cardRadius: theme.cardRadius,
    chipRadius: theme.chipRadius,
    ctrlRadius: theme.ctrlRadius,
    dangerText: theme.dangerText,
    editorial: theme.editorial,
    faint: theme.faint,
    ink: theme.ink,
    line: theme.line,
    linkText: theme.linkText,
    linkUnderline: theme.linkUnderline,
    muted: theme.muted,
    serif: theme.serif,
    successText: theme.successText,
    subtle: theme.subtle,
    surfaceBg: theme.surfaceBg,
    trayBg: theme.trayBg,
  };
}

/**
 * 打开之后才进来的消息不传 `entrance`：它们不属于开窗动画，照序号算会白等最多 760ms。
 */
export function resolveMessage(
  message: WidgetChatMessage,
  theme: MessageTheme,
  entrance?: MessageEntrance,
): ResolvedMessage {
  const isUser = message.role === "user";
  // 未送达不压透明度——压了文字对比度就保不住，买家恰恰最需要看清这句。
  // 换成淡 accent 底加深字，跟发出去的实心 accent 一眼分开。
  const undelivered = isUser && message.failed === true;
  const content =
    (isUser && message.textFormat !== "blocks") ||
    message.textFormat === "plain"
      ? {
          structured: false,
          blocks: message.text
            ? [{ id: "0", type: "text" as const, text: message.text }]
            : [],
        }
      : readReplyContent(message.text ?? "");
  const blocks = content.blocks;
  // Editorial 的 AI 回复直接排在面板上，像店铺自己的文案；买家消息仍是唯一的实色块。
  const bare = !isUser && theme.editorial;

  return {
    align: isUser ? "flex-end" : "flex-start",
    blocks,
    background: undelivered
      ? theme.accentSoft
      : isUser
        ? theme.accentFill
        : bare
          ? "transparent"
          : theme.bubbleBg,
    color: isUser ? (undelivered ? theme.ink : theme.accentFillFg) : theme.ink,
    compareCard: message.compareCard
      ? resolveCompareCard(message.compareCard)
      : undefined,
    delay:
      entrance === undefined
        ? `${String(message.typing ? THINKING_DELAY_MS : 0)}ms`
        : entrance.wake
          ? `${String(WAKE_DELAY_MS + Math.min(entrance.order, MAX_STAGGERED) * WAKE_STEP_MS)}ms`
          : `${String(ENTER_DELAY_MS + Math.min(entrance.order, MAX_STAGGERED) * STAGGER_STEP_MS)}ms`,
    wake: entrance?.wake === true,
    lineHeight: theme.editorial && theme.serif ? 1.55 : 1.5,
    // 原型的 82% 按整行算、只管文字区，左右 14px 内边距另加；AI 这一列已经让出了角色和间距，要折回整行宽度。
    maxWidth: isUser
      ? `min(100%, calc(${theme.editorial ? "84%" : "82%"} + 28px))`
      : bare
        ? "100%"
        : "min(100%, calc((100% + var(--gn-avatar-size) + var(--gn-avatar-gap)) * .82 + 28px))",
    message,
    padding: bare ? "0" : "11px 14px",
    products: (message.products ?? []).map((product) => ({
      ...product,
      hasImage: product.imageUrl !== undefined,
    })),
    radius: bare ? "0px" : theme.bubbleRadius,
    showText: blocks.some(
      (block) => block.type === "text" && block.text !== "",
    ),
    tokens: readTokens(theme),
  };
}
